import React, { useState } from 'react';
import { Copy, Check, ExternalLink, Save, Sparkles } from 'lucide-react';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';
import { Textarea } from '../design-system';

export default function AIAnalysisModal({ title, prompt, existingAnalysis, onClose, onSave }) {
  const [step, setStep] = useState(existingAnalysis?.response_text ? 'view' : 'prompt');
  const [copied, setCopied] = useState(false);
  const [response, setResponse] = useState(existingAnalysis?.response_text || '');
  const [saving, setSaving] = useState(false);

  const copyPrompt = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!response.trim()) return;
    setSaving(true);
    try {
      await onSave(response);
      setStep('view');
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
    setSaving(false);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={<span className="flex items-center gap-1"><Sparkles size={10} /> Apex AI Analysis</span>}
      size="lg"
    >
      {/* Steps indicator */}
      <div className="border-b border-zinc-800 -mx-5 px-5 pb-3 mb-5 flex items-center gap-2 text-[10px] tracking-wider uppercase">
        {[['prompt','1','Prompt'],['response','2','Coller la réponse'],['view','3','Historique']].map(([s,n,label],i,arr) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1 ${step === s ? 'text-cyan-400' : 'text-zinc-500'}`}>
              <div className={`w-5 h-5 border flex items-center justify-center font-bold ${step === s ? 'border-cyan-400 bg-cyan-400/10' : 'border-zinc-700'}`}>{n}</div>
              <span>{label}</span>
            </div>
            {i < arr.length - 1 && <div className="flex-1 h-px bg-zinc-800" />}
          </React.Fragment>
        ))}
      </div>

      <div className="space-y-4">
        {step === 'prompt' && (
          <>
            <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 text-sm">
              <div className="font-bold text-cyan-400 mb-1">Workflow</div>
              <ol className="text-zinc-300 space-y-1 text-xs list-decimal ml-4">
                <li>Clique sur "Copier le prompt" ci-dessous</li>
                <li>Ouvre Claude (Pro ou ton projet Apex) et colle le prompt</li>
                <li>Revient ici avec la réponse et passe à l'étape 2 pour la sauvegarder</li>
              </ol>
            </div>
            <div className="border border-zinc-800">
              <div className="border-b border-zinc-800 p-3 flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase font-semibold">Prompt généré</div>
                <Button variant="primary" size="sm" onClick={copyPrompt}>
                  {copied ? <><Check size={12} /> COPIÉ</> : <><Copy size={12} /> COPIER LE PROMPT</>}
                </Button>
              </div>
              <pre className="p-4 text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap max-h-[400px] overflow-y-auto">{prompt}</pre>
            </div>
            <div className="flex gap-2">
              <a href="https://claude.ai" target="_blank" rel="noopener noreferrer"
                className="flex-1 border border-zinc-800 text-zinc-300 p-3 hover:border-cyan-400 hover:text-cyan-400 transition font-semibold tracking-wider text-sm flex items-center justify-center gap-2">
                <ExternalLink size={14} /> OUVRIR CLAUDE
              </a>
              <Button variant="primary" className="flex-[2] p-3" onClick={() => setStep('response')}>
                J'AI LA RÉPONSE DE CLAUDE →
              </Button>
            </div>
          </>
        )}

        {step === 'response' && (
          <>
            <div className="border border-fuchsia-400/30 bg-fuchsia-400/5 p-3 text-sm">
              <div className="font-bold text-fuchsia-400 mb-1">Colle la réponse de Claude</div>
              <div className="text-zinc-300 text-xs">Ça sera sauvegardé dans l'historique de cette {existingAnalysis?.game_id ? 'game' : 'session'}.</div>
            </div>
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Colle ici la réponse complète de Claude..."
              rows={12}
              autoFocus
            />
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1 p-3" onClick={() => setStep('prompt')}>← RETOUR</Button>
              <Button variant="primary" className="flex-[2] p-3" onClick={handleSave} disabled={!response.trim() || saving} loading={saving}>
                <Save size={14} /> {saving ? 'SAUVEGARDE...' : "SAUVEGARDER L'ANALYSE"}
              </Button>
            </div>
          </>
        )}

        {step === 'view' && (
          <>
            <div className="border border-cyan-400/30 bg-cyan-400/5 p-3 flex items-center gap-3">
              <Sparkles size={16} className="text-cyan-400" />
              <div>
                <div className="font-bold text-cyan-400 text-sm">Analyse Apex</div>
                <div className="text-xs text-zinc-500 font-mono">{existingAnalysis?.created_at ? new Date(existingAnalysis.created_at).toLocaleString('fr-FR') : 'maintenant'}</div>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-5 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-[500px] overflow-y-auto">
              {response || existingAnalysis?.response_text}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1 p-3" onClick={() => setStep('response')}>MODIFIER</Button>
              <Button variant="secondary" className="flex-[2] p-3" onClick={onClose}>FERMER</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
